import { gql } from '@apollo/client'

export const SINGLE_PROJECT_MODEL = `
    id
    title
    description
    targetN
    service_type
    code
    type
    status
    calendly_link
    deletable
    email_templates{
        category
        subject
        content
        last_modify
    }
    owners {
        id
        email
    }
    pending_experts {
        email
        completedAt
    }
    keywords
    referable
    surveyLink
    start_date
    end_date
    jobs {
        user {
            id
        }
    }
    team {
        id
        name
        availableInvites
    }
`

export const LOAD_PROJECTS_QUERY = gql`
    query Projects(
        $query: String
        $filters: ProjectFilters!
        $pagination: Pagination
    ) {
        projects(query: $query, filters: $filters, pagination: $pagination) {
            total
            projects {
                id
                title
                targetN
                code
                type
                owners {
                    id
                    email
                }
                start_date
                end_date
                numAccepted
                numCompleted
                responseRate
            }
        }
    }
`

export const PROJECT_BASIC = gql`
    query Project($id: String!) {
        project(id: $id) {
            id
            status
            type
            service_type
            title
            adminNotes
            owners {
                id
                fullName
                email
            }
            description
            compensationType {
                name
                params
            }
            team {
                id
                name
            }
            qualtricsSurvey {
                surveyId
            }
        }
    }
`

export const PROJECT_RECOMMENDATIONS_QUERY = gql`
    query ProjectRecommendations($id: String!) {
        project(id: $id) {
            id
            expertRecommendations {
                sourceText
                generationStatus
                generationError
                lastGeneratedAt
                lastSearchedAt
                lastEvaluationScheduledAt
                uniqueExpertIds
                criteria {
                    criterionId
                    description
                    searchInputs {
                        queries
                        filters {
                            categoryId
                            values
                        }
                    }
                    expertIds
                    expertScores {
                        expertId
                        elasticsearchScore
                    }
                    evaluatedCount
                    total
                    searchedAt
                    searchStatus
                    searchError
                }
                evaluations {
                    expertId
                    expertName
                    score
                    verdict
                    evaluation
                    evaluationStatus
                    evaluatedAt
                }
            }
        }
    }
`

export const LOAD_SINGLE_PROJECT_QUERY = gql`
    query Project($id: String!) {
      project(id: $id) {
          ${SINGLE_PROJECT_MODEL}
      }
    }
`

export const LOAD_PROJECT_JOBS = gql`
    query Project($id: String!) {
        project(id: $id) {
            id
            jobs {
                id
                invite_status
                response_status
                overall_status
                user {
                    id
                    fullName
                    firstName
                    lastName
                    email
                }
                expert {
                    id
                    title
                    fullName
                    country
                    profile_picture
                    organization {
                        name
                    }
                    status
                    stripeAccount {
                        stripeId
                        isConnected
                    }
                    payment_notify
                }
            }
        }
    }
`

export const LOAD_SINGLE_MANAGED_PROJECT_QUERY = gql`
    query Project($id: String!) {
        project(id: $id) {
            id
            title
            type
            code
            description
            status
            keywords
            deletable
            calendar {
                date
            }
            owners {
                id
                fullName
                email
            }
            compensationType {
                name
                params
            }
            qualificationQuestions {
                id
                question
                questionType
                otherIncluded
                answerChoices
            }
            surveyLink
            qualtricsSurvey {
                surveyId
                status
            }
            start_date
            end_date
            targetN
            jobs {
                invite_status
                response_status
                overall_status
                user {
                    id
                    fullName
                    firstName
                    lastName
                    email
                    expert {
                        id
                        status
                    }
                }
                email_logs {
                    subject
                    sent_at
                }
            }
            team {
                id
                name
                creditBalance
            }
        }
    }
`

export const CREATE_PROJECT_MUTATION = gql`
    mutation createProject($input: ProjectInput!) {
        createProject(input: $input) {
            id
        }
    }
`

export const COMPLETE_PROJECT_MUTATION = gql`
    mutation completeProject($id: ID!) {
        completeProject(id: $id) {
            id
            status
        }
    }
`

export const UPDATE_PROJECT_MUTATION = gql`
    mutation UpdateProject($id: ID!, $input: ProjectInput!) {
        updateProject(id: $id, input: $input) {
            ${SINGLE_PROJECT_MODEL}
        }
    }
`

export const DELETE_PROJECT_MUTATION = gql`
    mutation DeleteProject($id: ID!) {
        deleteProject(id: $id) {
            id
        }
    }
`

export const APPROVE_PROJECT_MUTATION = gql`
    mutation ApproveProject($id: ID!, $compensationType: ProjectCompensationInput!) {
        approveProject(id: $id, compensationType: $compensationType) {
            ${SINGLE_PROJECT_MODEL}
        }
    }
`

export const RUN_PROJECT_EXPERT_RECOMMENDATIONS_MUTATION = gql`
    mutation RunProjectExpertRecommendations($projectId: ID!, $rerun: Boolean) {
        runProjectExpertRecommendations(projectId: $projectId, rerun: $rerun) {
            id
            expertRecommendations {
                generationStatus
                generationError
            }
        }
    }
`

export const CONTINUE_PROJECT_EXPERT_RECOMMENDATIONS_MUTATION = gql`
    mutation ContinueProjectExpertRecommendations($projectId: ID!, $criterionId: String!) {
        continueProjectExpertRecommendations(projectId: $projectId, criterionId: $criterionId) {
            id
            expertRecommendations {
                criteria {
                    criterionId
                    evaluatedCount
                    total
                }
            }
        }
    }
`
